import * as React from 'react';
import { LocateFixed, MapPin, Navigation, User, Warehouse } from 'lucide-react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { COUNTRY_OPTIONS, DEFAULT_MAP_CENTER } from '@klareospace/pharos-lib-front-utils';
import { ORDER_UPDATE_FIELD_CATEGORY, type Order } from '@klareospace/pharos-lib-types';
import {
  AddressMap,
  AddressSearchInput,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  ErrorBanner,
  IconText,
  Input,
  PhoneInput,
  ScrollArea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@klareospace/pharos-lib-web-utils';

import { HERE_ATTRIBUTION, HERE_TILE_URL } from '&/config';

import type { AddressEditVariant } from '../hooks/use-address-edit-form';
import { useAddressEditForm } from '../hooks/use-address-edit-form';
import { ORDER_FIELD_EDIT_STATE } from '../hooks/use-order-editability';
import { useOrderEditability } from '../hooks/use-order-editability';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AddressEditDialogProps {
  readonly onClose: () => void;
  readonly open: boolean;
  readonly order: Order;
  readonly variant: AddressEditVariant;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface LockedGroupProps {
  readonly children: React.ReactNode;
  readonly locked: boolean;
}

/** Wraps impacting address controls with the in-progress tooltip when locked; controls stay individually disabled. */
function LockedGroup({ children, locked }: LockedGroupProps): React.JSX.Element {
  const { t } = useTranslation('orders');
  if (!locked) return <>{children}</>;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="cursor-not-allowed">{children}</div>
        </TooltipTrigger>
        <TooltipContent>{t('drawer.edit.locked.inProgressTooltip')}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface ManualAddressFieldsProps {
  readonly coords: { lat: number; lng: number } | null;
  readonly form: ReturnType<typeof useAddressEditForm>['form'];
  readonly hasAddressForGeocode: boolean;
  readonly isGeocoding: boolean;
  readonly locked: boolean;
  readonly onGeocodeClick: () => void;
  readonly onMapPositionChange: (lat: number, lng: number) => void;
}

function ManualAddressFields({
  coords,
  form,
  hasAddressForGeocode,
  isGeocoding,
  locked,
  onGeocodeClick,
  onMapPositionChange,
}: ManualAddressFieldsProps): React.JSX.Element {
  const { t } = useTranslation('orders');
  const { control, register } = form;

  return (
    <div className="space-y-3 border-l-2 border-primary/20 pl-6">
      <div className="grid grid-cols-[100px_1fr] gap-3">
        <div className="space-y-1">
          <label className="text-sm font-medium">{t('drawer.edit.address.houseNumber')}</label>
          <Input disabled={locked} {...register('address.houseNumber')} />
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium">{t('drawer.edit.address.street')}</label>
          <Input disabled={locked} {...register('address.street')} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="text-sm font-medium">{t('drawer.edit.address.postalCode')}</label>
          <Input disabled={locked} {...register('address.postalCode')} />
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium">{t('drawer.edit.address.city')}</label>
          <Input disabled={locked} {...register('address.city')} />
        </div>
      </div>
      <div className="space-y-1">
        <label className="text-sm font-medium">{t('drawer.edit.address.country')}</label>
        <Controller
          control={control}
          name="address.country"
          render={({ field }): React.JSX.Element => (
            <Select disabled={locked} onValueChange={field.onChange} value={field.value}>
              <SelectTrigger>
                <SelectValue placeholder={t('drawer.edit.address.country')} />
              </SelectTrigger>
              <SelectContent className="max-h-60 overflow-y-auto" position="popper">
                {COUNTRY_OPTIONS.map((country) => (
                  <SelectItem key={country.code} value={country.code}>
                    {country.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </div>
      <div className="space-y-2">
        <span className="text-sm font-medium">{t('drawer.edit.address.coordinates')}</span>
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Navigation className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9 text-sm"
              disabled={locked}
              onChange={(e): void => {
                const lat = parseFloat(e.target.value);
                if (!isNaN(lat)) onMapPositionChange(lat, coords?.lng ?? 0);
              }}
              placeholder={t('drawer.edit.address.latitude')}
              step="0.000001"
              type="number"
              value={coords?.lat ?? ''}
            />
          </div>
          <div className="relative flex-1">
            <Navigation className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 rotate-90 text-muted-foreground" />
            <Input
              className="pl-9 text-sm"
              disabled={locked}
              onChange={(e): void => {
                const lng = parseFloat(e.target.value);
                if (!isNaN(lng)) onMapPositionChange(coords?.lat ?? 0, lng);
              }}
              placeholder={t('drawer.edit.address.longitude')}
              step="0.000001"
              type="number"
              value={coords?.lng ?? ''}
            />
          </div>
          <button
            className="flex h-9 shrink-0 items-center gap-1.5 rounded-md border-2 border-dashed border-primary/30 bg-primary/5 px-3 text-sm font-medium text-primary transition-colors hover:border-primary/50 hover:bg-primary/10"
            disabled={isGeocoding || !hasAddressForGeocode || locked}
            onClick={onGeocodeClick}
            type="button"
          >
            <LocateFixed className="h-4 w-4" />
            {t('drawer.edit.address.geocode')}
          </button>
        </div>
      </div>
    </div>
  );
}

interface AddressSectionProps {
  readonly formState: ReturnType<typeof useAddressEditForm>;
  readonly isAddressLocked: boolean;
}

/** The address search + manual-mode + map section — only rendered for the delivery variant. */
function AddressSection({ formState, isAddressLocked }: AddressSectionProps): React.JSX.Element {
  const { t } = useTranslation('orders');
  const {
    coords,
    form,
    handleBackToSearch,
    handleEnterManually,
    handleGeocodeClick,
    handleMapPositionChange,
    handleQueryChange,
    handleSuggestionSelect,
    hasAddressForGeocode,
    isGeocoding,
    isManualMode,
    isSearchLoading,
    isSelected,
    query,
    score,
    suggestions,
  } = formState;

  return (
    <section className="space-y-3">
      <IconText icon={MapPin} iconClassName="text-primary" size="xs">
        <h3 className="text-sm font-semibold text-foreground">{t('drawer.edit.address.sectionTitle')}</h3>
      </IconText>

      <LockedGroup locked={isAddressLocked}>
        {!isManualMode && (
          <div className="space-y-3">
            <AddressSearchInput
              emptyResultsLabel={t('drawer.edit.address.emptyResults')}
              enterManuallyLabel={t('drawer.edit.address.enterManually')}
              isLoading={isSearchLoading}
              isSelected={isSelected}
              onEnterManually={handleEnterManually}
              onQueryChange={handleQueryChange}
              onSelect={handleSuggestionSelect}
              placeholder={t('drawer.edit.address.searchPlaceholder')}
              query={query}
              readOnly={isAddressLocked}
              score={score}
              searchAriaLabel={t('drawer.edit.address.searchAriaLabel')}
              suggestions={suggestions}
            />
            {isSelected && (
              <button
                className="text-xs text-muted-foreground underline-offset-2 transition-colors hover:text-foreground hover:underline"
                onClick={handleEnterManually}
                type="button"
              >
                {t('drawer.edit.address.manualEntryLink')}
              </button>
            )}
          </div>
        )}

        {isManualMode && (
          <>
            <button
              className="w-fit text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
              onClick={handleBackToSearch}
              type="button"
            >
              {t('drawer.edit.address.backToSearch')}
            </button>
            <ManualAddressFields
              coords={coords}
              form={form}
              hasAddressForGeocode={hasAddressForGeocode}
              isGeocoding={isGeocoding}
              locked={isAddressLocked}
              onGeocodeClick={handleGeocodeClick}
              onMapPositionChange={handleMapPositionChange}
            />
          </>
        )}
      </LockedGroup>

      <div className="space-y-1">
        <label className="text-sm font-medium">{t('drawer.edit.address.addressLine2')}</label>
        <Input
          placeholder={t('drawer.edit.address.addressLine2Placeholder')}
          {...form.register('address.complement')}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="text-sm font-medium">{t('drawer.edit.address.floor')}</label>
          <Input {...form.register('address.floor')} />
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium">{t('drawer.edit.address.digicode')}</label>
          <Input {...form.register('address.digicode')} />
        </div>
      </div>

      <Controller
        control={form.control}
        name="address.hasElevator"
        render={({ field }): React.JSX.Element => (
          <div className="flex items-center gap-2">
            <Checkbox checked={field.value} onCheckedChange={field.onChange} />
            <label className="cursor-pointer text-sm font-normal">{t('drawer.edit.address.hasElevator')}</label>
          </div>
        )}
      />

      <LockedGroup locked={isAddressLocked}>
        <div className="relative">
          <AddressMap
            attribution={HERE_ATTRIBUTION}
            lat={coords?.lat ?? DEFAULT_MAP_CENTER.lat}
            lng={coords?.lng ?? DEFAULT_MAP_CENTER.lng}
            onPositionChange={handleMapPositionChange}
            tileUrl={HERE_TILE_URL}
          />
          {isAddressLocked && <div className="absolute inset-0 z-10 cursor-not-allowed rounded-lg bg-background/40" />}
        </div>
      </LockedGroup>

      <div className="space-y-1">
        <label className="text-sm font-medium">{t('drawer.edit.address.accessInstructions')}</label>
        <Input
          placeholder={t('drawer.edit.address.accessInstructionsPlaceholder')}
          {...form.register('address.additionalInformations')}
        />
      </div>
    </section>
  );
}

interface ContactSectionProps {
  readonly form: ReturnType<typeof useAddressEditForm>['form'];
}

function ContactSection({ form }: ContactSectionProps): React.JSX.Element {
  const { i18n, t: tCommon } = useTranslation('common');
  const { t } = useTranslation('orders');

  return (
    <section className="space-y-3 border-t pt-4">
      <IconText icon={User} iconClassName="text-primary" size="xs">
        <h3 className="text-sm font-semibold text-foreground">{t('drawer.edit.address.contactSectionTitle')}</h3>
      </IconText>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="text-sm font-medium">{t('drawer.edit.address.contactName')}</label>
          <Input {...form.register('contactName')} />
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium">{t('drawer.edit.address.phoneNumber')}</label>
          <Controller
            control={form.control}
            name="contactPhone"
            render={({ field, fieldState }): React.JSX.Element => (
              <>
                <PhoneInput
                  countryAriaLabel={tCommon('phoneInput.countryAriaLabel')}
                  emptyText={tCommon('phoneInput.noCountryFound')}
                  locale={i18n.language}
                  onChange={(value): void => {
                    field.onChange(value);
                    void form.trigger('contactPhone');
                  }}
                  searchPlaceholder={tCommon('phoneInput.searchPlaceholder')}
                  value={field.value}
                />
                {fieldState.error?.message && (
                  <p className="text-sm font-medium text-destructive">{fieldState.error.message}</p>
                )}
              </>
            )}
          />
        </div>
      </div>
    </section>
  );
}

interface ReadOnlySiteBlockProps {
  readonly siteName: string;
}

/** Shown in the pickup variant instead of an address section — pickup is site-based, not client-editable. */
function ReadOnlySiteBlock({ siteName }: ReadOnlySiteBlockProps): React.JSX.Element {
  const { t } = useTranslation('orders');

  return (
    <section className="space-y-1">
      <IconText icon={Warehouse} iconClassName="text-primary" size="xs">
        <h3 className="text-sm font-semibold text-foreground">{t('drawer.edit.address.pickupSiteLabel')}</h3>
      </IconText>
      <p className="text-sm text-foreground">{siteName}</p>
    </section>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Address / contact editing pop-in for the pickup and delivery legs. Pickup only exposes
 * the contact fields (pickup is site-based — no editable address); delivery exposes the
 * full search/manual-entry/map address flow plus contact.
 */
export function AddressEditDialog({ onClose, open, order, variant }: AddressEditDialogProps): React.JSX.Element {
  const { t } = useTranslation('orders');
  const isDelivery = variant === 'delivery';

  const formState = useAddressEditForm({ onSuccess: onClose, order, variant });
  const { errorMessage, form, isPending, onSubmit } = formState;
  const hasContactPhoneError = Boolean(form.formState.errors.contactPhone);

  // LOCKED here can only mean the in-progress lock — the opening pencil is already permission-gated.
  const { getFieldState } = useOrderEditability(order);
  const isAddressLocked =
    isDelivery && getFieldState(ORDER_UPDATE_FIELD_CATEGORY.PLANNING_IMPACTING) === ORDER_FIELD_EDIT_STATE.LOCKED;

  function handleOpenChange(nextOpen: boolean): void {
    if (!nextOpen) onClose();
  }

  return (
    <Dialog onOpenChange={handleOpenChange} open={open}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isDelivery ? <MapPin className="h-4 w-4" /> : <Warehouse className="h-4 w-4" />}
            {t(isDelivery ? 'drawer.edit.address.deliveryTitle' : 'drawer.edit.address.pickupContactTitle')}
          </DialogTitle>
        </DialogHeader>

        {errorMessage !== null && <ErrorBanner message={errorMessage} />}

        <ScrollArea className="max-h-[65vh]">
          {/* pe-4: extra clearance so focus rings aren't clipped under the scrollbar overlay. */}
          <div className="space-y-6 p-2 pe-4">
            {isDelivery ? (
              <AddressSection formState={formState} isAddressLocked={isAddressLocked} />
            ) : (
              <ReadOnlySiteBlock siteName={order.pickup.site.name} />
            )}
            <ContactSection form={form} />
          </div>
        </ScrollArea>

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button onClick={onClose} type="button" variant="outline">
            {t('drawer.edit.cancel')}
          </Button>
          <Button disabled={isPending || hasContactPhoneError} onClick={onSubmit} type="button">
            {t('drawer.edit.save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
